/** Keep defaults aligned with lewouhmart-backend/src/settings/storefront-theme.ts */

/**
 * Canonical Lewouhmart storefront hex palette (page, nav tiers, copy, brand, accent).
 * Primary + accent are deliberately restrained vs neon greens / alarm reds — closer to
 * major retailer restraint while staying recognizable as Lewouhmart.
 */
export const STOREFRONT_PALETTE = {
  pageBackground: '#f4f6f8',
  navMainBg: '#2c3838',
  navTopBarBg: '#3d4f4f',
  navForeground: '#f8fafc',
  /** Emerald-600: reads premium vs #00b853 “neon promo green”. */
  brandPrimary: '#059669',
  /** Orange-600: warm CTA / “mart” accent without #ff4b2b “error red” feel. */
  accent: '#ea580c',
} as const;

export type StorefrontChromeTheme = {
  bodyBg: string;
  navBg: string;
  navTopBg: string;
  navFg: string;
  navAccent: string;
  brandPrimary: string;
};

/**
 * Default storefront palette — keep in sync with `lewouhmart-backend/src/settings/storefront-theme.ts`.
 * Used when API/site content has no theme yet (same values as DB seed defaults after reset).
 */
export const FALLBACK_STOREFRONT_THEME: StorefrontChromeTheme = {
  bodyBg: STOREFRONT_PALETTE.pageBackground,
  navBg: STOREFRONT_PALETTE.navMainBg,
  navTopBg: STOREFRONT_PALETTE.navTopBarBg,
  navFg: STOREFRONT_PALETTE.navForeground,
  navAccent: STOREFRONT_PALETTE.accent,
  brandPrimary: STOREFRONT_PALETTE.brandPrimary,
};

function sanitizeHex(raw: string | undefined | null, fallback: string): string {
  const s = String(raw ?? '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(s)) return s.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(s)) {
    const r = s[1];
    const g = s[2];
    const b = s[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return fallback;
}

/** Append 2-digit hex alpha to 6-digit #RRGGBB (e.g. 1a ≈ 10% opacity). */
export function hexWithAlpha(hex: string, aa: string): string {
  const t = hex.trim().toLowerCase();
  const m = /^#([0-9a-f]{6})$/.exec(t);
  if (!m) return hex;
  return `#${m[1]}${aa}`;
}

export function parseStorefrontThemeFromSiteContent(data: {
  storefrontThemeBodyBg?: string;
  storefrontThemeNavBg?: string;
  storefrontThemeNavTopBg?: string;
  storefrontThemeNavFg?: string;
  storefrontThemeNavAccent?: string;
  storefrontThemeBrandPrimary?: string;
}): StorefrontChromeTheme {
  return {
    bodyBg: sanitizeHex(data.storefrontThemeBodyBg, FALLBACK_STOREFRONT_THEME.bodyBg),
    navBg: sanitizeHex(data.storefrontThemeNavBg, FALLBACK_STOREFRONT_THEME.navBg),
    navTopBg: sanitizeHex(data.storefrontThemeNavTopBg, FALLBACK_STOREFRONT_THEME.navTopBg),
    navFg: sanitizeHex(data.storefrontThemeNavFg, FALLBACK_STOREFRONT_THEME.navFg),
    navAccent: sanitizeHex(data.storefrontThemeNavAccent, FALLBACK_STOREFRONT_THEME.navAccent),
    brandPrimary: sanitizeHex(
      data.storefrontThemeBrandPrimary,
      FALLBACK_STOREFRONT_THEME.brandPrimary,
    ),
  };
}
